/**
 * Questionnaire Service
 * API functions for questionnaire management
 */

const API_BASE = '/api/questionnaire'

/**
 * Get authorization headers with token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken')
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }
}

/**
 * Handle API response
 */
const handleResponse = async (response) => {
  const data = await response.json()
  
  if (!response.ok) {
    throw new Error(data.error?.message || data.error || 'Une erreur est survenue')
  }
  
  return data
}

/**
 * Get active questionnaire
 */
export const getActiveQuestionnaire = async () => {
  const response = await fetch(`${API_BASE}/active`)
  return handleResponse(response)
}

/**
 * Get all questionnaire versions
 * @param {boolean} includeDeleted - Include soft-deleted versions
 */
export const getAllVersions = async (includeDeleted = false) => {
  const url = `${API_BASE}/versions${includeDeleted ? '?includeDeleted=true' : ''}`
  const response = await fetch(url, {
    headers: getAuthHeaders()
  })
  return handleResponse(response)
}

/**
 * Get specific questionnaire version
 * @param {string} version - Version number
 */
export const getQuestionnaireByVersion = async (version) => {
  const response = await fetch(`${API_BASE}/${encodeURIComponent(version)}`, {
    headers: getAuthHeaders()
  })
  return handleResponse(response)
}

/**
 * Get questionnaire statistics
 */
export const getQuestionnaireStats = async () => {
  const response = await fetch(`${API_BASE}/stats`, {
    headers: getAuthHeaders()
  })
  return handleResponse(response)
}

/**
 * Create new questionnaire version
 * @param {Object} data - { questionnaire, metadata }
 */
export const createQuestionnaireVersion = async (data) => {
  const response = await fetch(API_BASE, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  })
  return handleResponse(response)
}

/**
 * Activate a questionnaire version
 * @param {string} version - Version number
 */
export const activateVersion = async (version) => {
  const response = await fetch(`${API_BASE}/${encodeURIComponent(version)}/activate`, {
    method: 'PUT',
    headers: getAuthHeaders()
  })
  return handleResponse(response)
}

/**
 * Delete a questionnaire version (soft delete)
 * @param {string} version - Version number
 */
export const deleteVersion = async (version) => {
  const response = await fetch(`${API_BASE}/${encodeURIComponent(version)}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  })
  return handleResponse(response)
}

/**
 * Restore a deleted questionnaire version
 * @param {string} version - Version number
 */
export const restoreVersion = async (version) => {
  const response = await fetch(`${API_BASE}/${encodeURIComponent(version)}/restore`, {
    method: 'PUT',
    headers: getAuthHeaders()
  })
  return handleResponse(response)
}

/**
 * Compare two questionnaire versions
 * @param {string} v1 - First version
 * @param {string} v2 - Second version
 */
export const compareVersions = async (v1, v2) => {
  const response = await fetch(`${API_BASE}/compare/${encodeURIComponent(v1)}/${encodeURIComponent(v2)}`, {
    headers: getAuthHeaders()
  })
  return handleResponse(response)
}

export default {
  getActiveQuestionnaire,
  getAllVersions,
  getQuestionnaireByVersion,
  getQuestionnaireStats,
  createQuestionnaireVersion,
  activateVersion,
  deleteVersion,
  restoreVersion,
  compareVersions
}

